import { Fragment } from 'react';
import { linkify } from '@/lib/linkify';
import { cn } from '@/lib/utils';

/**
 * Plain text with its URLs turned into links.
 *
 * `linkify` decides what becomes a link and what the destination is — including
 * refusing `javascript:` and friends — so this component only renders. See
 * `lib/linkify.ts` for the rules.
 *
 * `target="_blank"` because these appear inside a page somebody is part-way
 * through: opening a Meet room in place would lose an announcement they had not
 * finished reading. `rel="noopener noreferrer"` goes with it, always — without
 * `noopener` the opened page can navigate this one, which is the whole tabnabbing
 * trick.
 */
export function LinkifiedText({
  text,
  className,
  linkClassName,
}: {
  text: string;
  className?: string;
  linkClassName?: string;
}) {
  const segments = linkify(text);

  return (
    // `whitespace-pre-wrap`: the body is plain text somebody typed, so their
    // line breaks are part of the message.
    <span className={cn('whitespace-pre-wrap', className)}>
      {segments.map((segment, i) =>
        segment.kind === 'link' ? (
          <a
            key={i}
            href={segment.href}
            target="_blank"
            rel="noopener noreferrer"
            // Stops a click reaching a card that would otherwise open a dialog
            // or navigate — the link is the more specific intent.
            onClick={(e) => e.stopPropagation()}
            className={cn(
              'break-words font-medium text-brand-700 underline underline-offset-2 hover:text-brand-800',
              linkClassName,
            )}
          >
            {segment.value}
          </a>
        ) : (
          <Fragment key={i}>{segment.value}</Fragment>
        ),
      )}
    </span>
  );
}
